import { useRef, useState } from 'react';
import ContentLoader from 'react-content-loader';

import useOnMount from '../../../../hooks/useOnMount';
import COLORS from '../../../../theme/colors';

const PACKAGE_SUMMARY_RIGHT_WIDTH = 120;
const MATCH_CONFIDENCE_RIGHT_WIDTH = 70;
const LICENSES_GAP = 40;

/**
 * Draws a content loader while the drawer is loading.
 */
const DrawerLoading = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  // On mount, set the width of the loader to match the width of the drawer.
  useOnMount(() => {
    if (ref.current) {
      setWidth(ref.current.offsetWidth);
    }
  });

  // Compute right X positions based on the current width.
  const packageSummaryRightX = width - PACKAGE_SUMMARY_RIGHT_WIDTH;
  const matchConfidenceRightX = width - MATCH_CONFIDENCE_RIGHT_WIDTH;

  // Give each license column (three total) equal width.
  const licensesWidth = (width - LICENSES_GAP * 2) / 3;
  const licenseLeftX = 20;
  const licenseMiddleX = licenseLeftX + licensesWidth + LICENSES_GAP;
  const licenseRightX = licenseMiddleX + licensesWidth + LICENSES_GAP;

  return (
    <div ref={ref}>
      <ContentLoader
        speed={2}
        width="100%"
        height={700}
        foregroundColor={COLORS.fossaGray20}
        backgroundColor={COLORS.fossaGray10}
      >
        {/* Header: Package Name and Version */}
        <rect x="20" y="20" rx="4" ry="4" width="250" height="28" />
        <rect x="20" y="52" rx="3" ry="3" width="350" height="16" />

        {/* Buttons: Reject, Download Match Data */}
        <rect x="20" y="88" rx="4" ry="4" width="80" height="32" />
        <rect x="110" y="88" rx="4" ry="4" width="150" height="32" />

        {/* Tabs: Details, Compare Code */}
        <rect x="20" y="140" rx="0" ry="0" width="60" height="24" />
        <rect x="90" y="140" rx="0" ry="0" width="100" height="24" />
        <rect x="0" y="172" rx="0" ry="0" width="100%" height="1" />

        {/* Package Summary - Section Title */}
        <rect x="20" y="200" rx="3" ry="3" width="200" height="20" />
        {/* Package Summary - 5 rows */}
        <rect x="20" y="230" rx="3" ry="3" width="150" height="14" />
        <rect x={packageSummaryRightX} y="230" rx="3" ry="3" width="120" height="14" />
        <rect x="20" y="250" rx="3" ry="3" width="150" height="14" />
        <rect x={packageSummaryRightX} y="250" rx="3" ry="3" width="120" height="14" />
        <rect x="20" y="270" rx="3" ry="3" width="150" height="14" />
        <rect x={packageSummaryRightX} y="270" rx="3" ry="3" width="120" height="14" />
        <rect x="20" y="290" rx="3" ry="3" width="150" height="14" />
        <rect x={packageSummaryRightX} y="290" rx="3" ry="3" width="120" height="14" />
        <rect x="20" y="310" rx="3" ry="3" width="150" height="14" />
        <rect x={packageSummaryRightX} y="310" rx="3" ry="3" width="120" height="14" />

        {/* Match Confidence - Section Title */}
        <rect x="20" y="350" rx="3" ry="3" width="200" height="20" />
        {/* Match Confidence - Confidence Bar */}
        <rect x="20" y="380" rx="4" ry="4" width="400" height="24" />
        {/* Match Confidence - 7 rows */}
        <rect x="20" y="414" rx="3" ry="3" width="250" height="14" />
        <rect x={matchConfidenceRightX} y="414" rx="3" ry="3" width="70" height="14" />
        <rect x="20" y="434" rx="3" ry="3" width="250" height="14" />
        <rect x={matchConfidenceRightX} y="434" rx="3" ry="3" width="70" height="14" />
        <rect x="20" y="454" rx="3" ry="3" width="250" height="14" />
        <rect x={matchConfidenceRightX} y="454" rx="3" ry="3" width="70" height="14" />
        <rect x="20" y="474" rx="3" ry="3" width="250" height="14" />
        <rect x={matchConfidenceRightX} y="474" rx="3" ry="3" width="70" height="14" />
        <rect x="20" y="494" rx="3" ry="3" width="250" height="14" />
        <rect x={matchConfidenceRightX} y="494" rx="3" ry="3" width="70" height="14" />
        <rect x="20" y="514" rx="3" ry="3" width="250" height="14" />
        <rect x={matchConfidenceRightX} y="514" rx="3" ry="3" width="70" height="14" />
        <rect x="20" y="534" rx="3" ry="3" width="250" height="14" />
        <rect x={matchConfidenceRightX} y="534" rx="3" ry="3" width="70" height="14" />

        {/* Licenses - Section Title */}
        <rect x="20" y="574" rx="3" ry="3" width="150" height="20" />
        {/* Licenses - Assuming 4 rows */}
        <rect x={licenseLeftX} y="604" rx="3" ry="3" width={licensesWidth} height="14" />
        <rect x={licenseMiddleX} y="604" rx="3" ry="3" width={licensesWidth} height="14" />
        <rect x={licenseRightX} y="604" rx="3" ry="3" width={licensesWidth} height="14" />

        <rect x={licenseLeftX} y="624" rx="3" ry="3" width={licensesWidth} height="14" />
        <rect x={licenseMiddleX} y="624" rx="3" ry="3" width={licensesWidth} height="14" />
        <rect x={licenseRightX} y="624" rx="3" ry="3" width={licensesWidth} height="14" />

        <rect x={licenseLeftX} y="644" rx="3" ry="3" width={licensesWidth} height="14" />
        <rect x={licenseMiddleX} y="644" rx="3" ry="3" width={licensesWidth} height="14" />
        <rect x={licenseRightX} y="644" rx="3" ry="3" width={licensesWidth} height="14" />

        <rect x={licenseLeftX} y="664" rx="3" ry="3" width={licensesWidth} height="14" />
        <rect x={licenseMiddleX} y="664" rx="3" ry="3" width={licensesWidth} height="14" />
        <rect x={licenseRightX} y="664" rx="3" ry="3" width={licensesWidth} height="14" />
      </ContentLoader>
    </div>
  );
};

export default DrawerLoading;
